import { motion } from "framer-motion";
import { TrendingUp, Wallet, Landmark, Compass, LineChart, Rocket } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const reveal = {
  hidden: { opacity: 0, y: 32 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: EASE, delay: i * 0.1 },
  }),
};

const learnItems = [
  { icon: Wallet, title: "Where your cash actually goes", text: "A simple way to read your numbers without an accounting degree." },
  { icon: TrendingUp, title: "Get paid faster", text: "Practical tactics to shorten payment times and chase less." },
  { icon: Landmark, title: "Never fear BAS again", text: "How to provision for tax and large payments before they land." },
  { icon: Compass, title: "A 13-week cash flow rhythm", text: "The forecasting habit that keeps you ahead, not catching up." },
  { icon: LineChart, title: "Read the early warnings", text: "Spot a cash squeeze months before it bites, and act early." },
  { icon: Rocket, title: "Fund growth safely", text: "Grow the business without putting day-to-day cash under pressure." },
];

const audiences = [
  { title: "Established owners", text: "You're turning over good money but the bank balance never seems to show it." },
  { title: "Growing businesses", text: "Sales are climbing, and somehow cash feels tighter than ever." },
  { title: "Seasonal operators", text: "Feast one quarter, famine the next. You want a smoother ride." },
];

export default function Chapters() {
  return (
    <div data-testid="numbered-chapters">
      {/* 01 — Why cash flow */}
      <section id="why" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="lg:col-span-5"
          >
            <p className="font-mono-brand text-sm font-semibold text-[#0FA3E8]">/ 01</p>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2B4B]">
              Why cash flow and why now
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              Profit tells you how your business is performing. Cash flow determines what you
              can do next.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Even a profitable business can experience cash flow pressure when the timing of
              money coming in does not match the timing of payments going out.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              This session is about helping you take better control of that timing. You will
              learn practical ways to understand where your cash is going, anticipate what is
              coming, and make better financial decisions.
            </p>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Because cash flow is not just something to manage when times are tough. It is a
              fundamental part of running and growing a healthy business.
            </p>
          </motion.div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { stat: "~80%", text: "of Australian small and medium businesses have experienced cash flow challenges (CommBank small business research, 2025)" },
              { stat: "1 in 3", text: "small-business invoices paid late. Only 68.1% were paid on time (Payment Times Reporting Regulator, July 2025)" },
              { stat: "64 days", text: "for large businesses to pay 95% of small-business invoices, up from 58 days in the previous cycle (regulator analysis, January 2026)" },
            ].map((card, i) => (
              <motion.div
                key={card.stat}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-80px" }}
                className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[#0FA3E8]/40 hover:shadow-lg"
              >
                <p className="font-mono-brand text-3xl font-semibold text-[#0B2B4B]">{card.stat}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 02 — What you'll learn */}
      <section id="learn" className="relative overflow-hidden bg-[#0B2B4B] py-20 md:py-28 grain">
        <div
          aria-hidden
          className="pointer-events-none absolute -bottom-48 -left-48 h-[30rem] w-[30rem] rounded-full bg-[#0FA3E8]/15 blur-3xl"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="max-w-2xl"
          >
            <p className="font-mono-brand text-sm font-semibold text-[#38BDF8]">/ 02</p>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-white">
              What you'll walk away with
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-300">
              No jargon, no 40-tab spreadsheets. Six practical takeaways you can put to work in
              your business immediately.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {learnItems.map((item, i) => (
              <motion.div
                key={item.title}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="group rounded-2xl border border-white/10 bg-[#16345C]/60 p-6 backdrop-blur transition-all duration-200 hover:-translate-y-1 hover:border-[#0FA3E8]/50"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#0FA3E8]/15 text-[#38BDF8] transition-colors duration-200 group-hover:bg-[#0FA3E8] group-hover:text-white">
                  <item.icon className="h-5 w-5" />
                </span>
                <h3 className="font-display mt-5 text-lg font-semibold text-white">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-300">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 03 — Who it's for */}
      <section id="who" className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            variants={reveal}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-80px" }}
            className="max-w-2xl"
          >
            <p className="font-mono-brand text-sm font-semibold text-[#0FA3E8]">/ 03</p>
            <h2 className="font-display mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B2B4B]">
              Who this is for
            </h2>
            <p className="mt-5 text-base leading-relaxed text-slate-600">
              If any of these sound familiar, you'll get real value from this session.
            </p>
          </motion.div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
            {audiences.map((a, i) => (
              <motion.div
                key={a.title}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: "-60px" }}
                className="relative rounded-2xl border border-slate-200/80 bg-white p-7 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:border-[#0FA3E8]/40 hover:shadow-lg"
              >
                <span className="font-mono-brand text-xs font-semibold text-[#0FA3E8]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display mt-3 text-xl font-semibold text-[#0B2B4B]">{a.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">{a.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
